import { Absence } from "../domain/Absence";
import { AbsenceRepo } from "../domain/AbsenceRepo";
import { sync_command_dispatcher, query_dispatcher } from "@/api/client";

export class APIAbsenceRepository implements AbsenceRepo {

  private getCompanyDbId(): number | null {
    // Extraer companydb_id de la URL actual
    const pathMatch = window.location.pathname.match(/\/(\d+)\//);
    return pathMatch ? parseInt(pathMatch[1]) : null;
  }

  async find_all(user_id?: number): Promise<Absence[]> {
    const payload: any = {};

    // Si se proporciona user_id, incluirlo en el query
    if (user_id) {
      payload.user_id = user_id;
    }

    const result = await query_dispatcher.dispatch("AbsenceByUserQuery", payload);

    return result.map((a: any) => {
      return new Absence(
        a.uuid,
        new Date(a.start).toISOString(),
        new Date(a.end).toISOString(),
        a.type || a.reason || "",
        a.status || 'pending',
        a.fotoJustificante || null,
        a.lista_motivos || null,
        a.tipoAusencia || null
      );
    });
  }

  async find_by_criteria(criteria: any[][], order_by?: string[], limit?: number, offset?: number): Promise<Absence[]> {
    const payload = {
      filters: criteria,  // Los filtros incluyen ['user_id', '==', selectedUserId]
      limit,
      offset,
      order_by: order_by || ['-start']
    };
    const response = await query_dispatcher.dispatch('filter_absences.query', payload);

    return response.map((a: any) => {
      return new Absence(
        a.uuid,
        new Date(a.start).toISOString(),
        new Date(a.end).toISOString(),
        a.type || a.reason || "",
        a.status || 'pending',
        a.fotoJustificante || null,
        a.lista_motivos || null,
        a.tipoAusencia || null
      );
    });
  }

  async save(absence: Absence): Promise<void> {
    // Obtener companydb_id desde la ruta
    const companydb_id = this.getCompanyDbId();

    const payload = {
      cmd_name: "create_absence",
      uuid: absence.uuid,
      start: absence.start,
      end: absence.end,
      reason: absence.reason,
      fotoJustificante: absence.fotoJustificante || null,
      lista_motivos: absence.lista_motivos || null,
      tipoAusencia: absence.tipoAusencia || null,
      send_email_notification: absence.sendEmailNotification || false,
      companydb_id: companydb_id,
    };

    const response = await sync_command_dispatcher.dispatch("create_absence", payload);

    // El backend devuelve un AsyncJob con output que puede contener el error
    // Verificar si el job falló
    if (response && response.output) {
      const output = response.output;

      // Si el output es un string y contiene "error" o si tiene campos de error
      if (typeof output === 'string') {
        // El output puede ser un mensaje de error
        if (output.toLowerCase().includes('error') || output.toLowerCase().includes('valueerror')) {
          throw new Error(output);
        }
      } else if (typeof output === 'object') {
        // El output puede ser un objeto con error
        if (output.error) {
          throw new Error(output.error);
        }
        if (output.status === 'error') {
          throw new Error(output.message || 'Error al crear la ausencia');
        }
      }
    }

    // Verificar campos de error directos en la respuesta
    if (response && response.error) {
      throw new Error(response.error);
    }
    if (response && response.status === 'error') {
      throw new Error(response.message || 'Error al crear la ausencia');
    }

    return response;
  }

  async update(absence: Absence): Promise<void> {
    const payload = {
      cmd_name: "update_absence",
      uuid: absence.uuid,
      user_id: absence.user_id,
      start: absence.start,
      end: absence.end,
      reason: absence.reason,
      fotoJustificante: absence.fotoJustificante || null,
      lista_motivos: absence.lista_motivos || null,
      tipoAusencia: absence.tipoAusencia || null,
      status: absence.status || 'pending',
    };

    const response = await sync_command_dispatcher.dispatch("update_absence", payload);

    // El backend devuelve un AsyncJob con output que puede contener el error
    // Verificar si el job falló
    if (response && response.output) {
      const output = response.output;

      // Si el output es un string y contiene "error" o si tiene campos de error
      if (typeof output === 'string') {
        // El output puede ser un mensaje de error
        if (output.toLowerCase().includes('error') || output.toLowerCase().includes('valueerror')) {
          throw new Error(output);
        }
      } else if (typeof output === 'object') {
        // El output puede ser un objeto con error
        if (output.error) {
          throw new Error(output.error);
        }
        if (output.status === 'error') {
          throw new Error(output.message || 'Error al crear la ausencia');
        }
      }
    }

    // Verificar campos de error directos en la respuesta
    if (response && response.error) {
      throw new Error(response.error);
    }
    if (response && response.status === 'error') {
      throw new Error(response.message || 'Error al crear la ausencia');
    }

    return response;
  }
}
